/**
 * DataSection - Data export functionality for admins
 *
 * Allows organization admins to export all functional data
 * for their organization as a structured JSON file.
 */

import { useState } from 'react';
import { Download, Database, AlertCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, Button, Spinner } from '@/components/ui';
import { useSettingsStore, useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';

export function DataSection() {
  const { organization } = useSettingsStore();
  const { user } = useAuthStore();
  const toast = useToast();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!user || !organization) {
      toast.error('Unable to export: missing user or organization context');
      return;
    }

    setExporting(true);
    try {
      const functions = getFunctions();
      const exportData = httpsCallable(functions, 'exportOrganizationData');

      const result = await exportData({});
      const data = result.data as {
        success: boolean;
        data?: object;
        error?: string;
      };

      if (!data.success) {
        throw new Error(data.error || 'Export failed');
      }

      // Generate filename with org name and timestamp
      const sanitizedOrgName = organization.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `perfin-export-${sanitizedOrgName}-${timestamp}.json`;

      // Create and download file
      const jsonString = JSON.stringify(data.data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(
        error instanceof Error
          ? `Export failed: ${error.message}`
          : 'Failed to export data'
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-gray-400" />
            <CardTitle>Data Export</CardTitle>
          </div>
        </CardHeader>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Export all your organization's data as a single JSON file. This includes:
          </p>

          <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 ml-2">
            <li>All items (tasks, transactions, notes, key dates) and their sub-items</li>
            <li>Categories and sub-categories</li>
            <li>Entities and accounts</li>
            <li>Organization members and pending invitations</li>
            <li>Notifications</li>
            <li>All metadata (timestamps, attachments info, reminders)</li>
          </ul>

          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Security Note</p>
              <p>Authentication credentials and MFA data are never exported.
                 Attachment files are not included—only their metadata (names, sizes, URLs).</p>
            </div>
          </div>

          <div className="pt-2">
            <Button
              onClick={handleExport}
              disabled={exporting}
              leftIcon={exporting ? <Spinner size="sm" /> : <Download className="h-4 w-4" />}
            >
              {exporting ? 'Exporting...' : 'Export All Data'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
